import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, X, GripVertical, ExternalLink } from 'lucide-react';
import { API } from '../../contexts/AuthContext';
import { FileUpload } from '../../components/admin/FileUpload';

const TIERS = [
  { value: 'corporate', label: 'Corporate Member' },
  { value: 'platinum', label: 'Platinum Sponsor' },
  { value: 'gold', label: 'Gold Sponsor' },
  { value: 'silver', label: 'Silver Sponsor' },
];

export default function SponsorsAdmin() {
  const [sponsors, setSponsors] = useState([]);
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', logo_url: '', website_url: '', tier: 'corporate', event_id: '', heading: '', order: 0 });
  const [toast, setToast] = useState('');

  const token = localStorage.getItem('idsea_token');
  const headers = { Authorization: `Bearer ${token}` };
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = async () => {
    try {
      const [sr, er] = await Promise.all([
        axios.get(`${API}/admin/sponsors`, { headers }),
        axios.get(`${API}/admin/events`, { headers }).catch(() => ({ data: [] }))
      ]);
      setSponsors(sr.data || []);
      setEvents(er.data || []);
    } catch { }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!form.name || !form.logo_url) { showToast('Name and logo required'); return; }
    try {
      if (editItem) {
        await axios.put(`${API}/admin/sponsors/${editItem.id}`, form, { headers });
        showToast('Sponsor updated');
      } else {
        await axios.post(`${API}/admin/sponsors`, form, { headers });
        showToast('Sponsor added');
      }
      setShowModal(false);
      setEditItem(null);
      setForm({ name: '', logo_url: '', website_url: '', tier: 'corporate', event_id: '', heading: '', order: 0 });
      load();
    } catch (e) { showToast('Error: ' + (e.response?.data?.detail || 'Failed')); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sponsor?')) return;
    await axios.delete(`${API}/admin/sponsors/${id}`, { headers });
    showToast('Deleted');
    load();
  };

  const openEdit = (s) => {
    setEditItem(s);
    setForm({ name: s.name, logo_url: s.logo_url, website_url: s.website_url || '', tier: s.tier || 'corporate', event_id: s.event_id || '', heading: s.heading || '', order: s.order || 0 });
    setShowModal(true);
  };

  const openAdd = () => {
    setEditItem(null);
    setForm({ name: '', logo_url: '', website_url: '', tier: 'corporate', event_id: '', heading: '', order: 0 });
    setShowModal(true);
  };

  const globalSponsors = sponsors.filter(s => !s.event_id);
  const eventSponsors = sponsors.filter(s => s.event_id);

  return (
    <div>
      {toast && <div className="toast-success" data-testid="toast">{toast}</div>}
      <div className="page-header">
        <h1 className="page-title" data-testid="sponsors-title">Corporate Members & Sponsors</h1>
        <button onClick={openAdd} className="btn-primary" data-testid="add-sponsor-btn"><Plus size={16} /> Add Sponsor</button>
      </div>

      {/* Global / Homepage Sponsors */}
      <div className="admin-card" style={{ marginBottom: '20px' }}>
        <h3 style={{ fontFamily: 'Poppins', fontSize: '15px', fontWeight: 700, color: '#0c3c60', marginBottom: '16px' }}>Homepage — Corporate Members</h3>
        {globalSponsors.length === 0 ? (
          <p style={{ color: '#9ca3af', fontSize: '13px' }}>No homepage sponsors added yet. Click "Add Sponsor" to add logos that scroll on the homepage.</p>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
            {globalSponsors.map(s => (
              <SponsorCard key={s.id} sponsor={s} onEdit={() => openEdit(s)} onDelete={() => handleDelete(s.id)} />
            ))}
          </div>
        )}
      </div>

      {/* Event-specific Sponsors */}
      {eventSponsors.length > 0 && (
        <div className="admin-card">
          <h3 style={{ fontFamily: 'Poppins', fontSize: '15px', fontWeight: 700, color: '#0c3c60', marginBottom: '16px' }}>Event Sponsors</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
            {eventSponsors.map(s => {
              const evt = events.find(e => e.id === s.event_id);
              return <SponsorCard key={s.id} sponsor={s} eventName={evt?.title} onEdit={() => openEdit(s)} onDelete={() => handleDelete(s.id)} />;
            })}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontFamily: 'Poppins', fontSize: '17px', fontWeight: 700, color: '#0c3c60', margin: 0 }}>{editItem ? 'Edit Sponsor' : 'Add Sponsor'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={20} /></button>
            </div>
            <div className="form-group"><label className="form-label">Name *</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="form-input" placeholder="Company name" data-testid="sponsor-name" /></div>
            <div className="form-group">
              <label className="form-label">Logo *</label>
              <FileUpload accept="image/*" label="Upload Logo" onUpload={(url) => setForm(f => ({ ...f, logo_url: url }))} />
              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Recommended: 400x200px PNG with transparent background. Max 2MB.</p>
              {form.logo_url && <img src={form.logo_url.startsWith('/api') ? `${API.replace('/api', '')}${form.logo_url}` : form.logo_url} alt="" style={{ height: '60px', objectFit: 'contain', marginTop: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', padding: '8px', background: '#fafbfc' }} />}
            </div>
            <div className="form-group"><label className="form-label">Website URL</label><input value={form.website_url} onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))} className="form-input" placeholder="https://company.com" /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group"><label className="form-label">Tier</label>
                <select value={form.tier} onChange={e => setForm(f => ({ ...f, tier: e.target.value }))} className="form-select" data-testid="sponsor-tier">
                  {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Display Order</label>
                <input type="number" value={form.order} onChange={e => setForm(f => ({ ...f, order: parseInt(e.target.value) || 0 }))} className="form-input" />
              </div>
            </div>
            <div className="form-group"><label className="form-label">Custom Heading</label><input value={form.heading} onChange={e => setForm(f => ({ ...f, heading: e.target.value }))} className="form-input" placeholder="e.g. Our Platinum Sponsors (overrides default)" /></div>
            <div className="form-group"><label className="form-label">Link to Event (optional)</label>
              <select value={form.event_id} onChange={e => setForm(f => ({ ...f, event_id: e.target.value }))} className="form-select" data-testid="sponsor-event">
                <option value="">Homepage (Global)</option>
                {events.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
              </select>
              <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>Leave as "Homepage" for the main site. Select an event to show only on that event's page.</p>
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleSave} className="btn-primary" data-testid="save-sponsor-btn">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SponsorCard({ sponsor, eventName, onEdit, onDelete }) {
  const logoSrc = sponsor.logo_url?.startsWith('/api') ? `${API.replace('/api', '')}${sponsor.logo_url}` : sponsor.logo_url;
  const tierColors = { corporate: '#0c3c60', platinum: '#6b7280', gold: '#d97706', silver: '#9ca3af' };
  return (
    <div style={{ background: '#fafbfc', borderRadius: '10px', padding: '14px', border: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60px', background: 'white', borderRadius: '8px', padding: '8px' }}>
        <img src={logoSrc} alt={sponsor.name} style={{ maxHeight: '50px', maxWidth: '100%', objectFit: 'contain' }} />
      </div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', textAlign: 'center' }}>{sponsor.name}</div>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
        <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: `${tierColors[sponsor.tier] || '#6b7280'}15`, color: tierColors[sponsor.tier] || '#6b7280', fontWeight: 700, textTransform: 'capitalize' }}>{sponsor.tier}</span>
        {eventName && <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '10px', background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>{eventName}</span>}
      </div>
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginTop: '4px' }}>
        <button onClick={onEdit} className="btn-secondary" style={{ fontSize: '11px', padding: '4px 10px' }}>Edit</button>
        <button onClick={onDelete} style={{ fontSize: '11px', padding: '4px 10px', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
      </div>
    </div>
  );
}
